// -- [Fetch current-data API, compute live estimates, 10s tick, refetch scheduler]

import { useState, useEffect, useRef, useCallback } from 'react'
import type { RoleKey } from '@/types/content'
import {
  fetchCurrentData,
  SECONDS_PER_91_25_DAYS,
  getCurrentDataEndpoint,
  FALLBACK_CURRENT_DATA,
  FALLBACK_CACHED_AT,
  FALLBACK_SERVER_TIME,
  type CurrentData,
} from '@/lib/currentDataApi'

const CACHE_TTL_SECONDS = 4800
const REFETCH_DELAY_MIN = 120
const REFETCH_DELAY_MAX = 240
const TICK_INTERVAL_MS = 10_000

function randomBetween(min: number, max: number): number {
  return min + Math.random() * (max - min)
}

function estimateAtTime(
  baseValue: number,
  ratePerSecond: number,
  cachedAt: number,
  tUnix: number
): number {
  const elapsed = Math.max(0, tUnix - cachedAt)
  return baseValue + ratePerSecond * elapsed
}

export interface CurrentDataDisplay {
  estimatedTotalPages: number
  estimatedPages: number
  activeDealerships: number
  estimatedTransactionsAud: number
  /** Live-estimated total time saved in seconds (from totalTimeSavedSeconds + rate × elapsed) */
  estimatedTotalTimeSavedSeconds: number
}

export interface UseCurrentDataResult {
  display: CurrentDataDisplay | null
  /** Raw API data for breakdown (same as used for display; null when using fallback only) */
  data: CurrentData | null
  loading: boolean
  error: boolean
  useFallback: boolean
}

function getPagesBaseAndRate(
  data: CurrentData,
  roleKey: RoleKey
): { base: number; ratePerSecond: number } {
  if (roleKey === 'management') {
    return {
      base: data.totalPagesStored,
      ratePerSecond: data.totalPagesStoredLast3Months / SECONDS_PER_91_25_DAYS,
    }
  }
  const byType = data.pagesByType[roleKey]
  if (!byType) {
    return {
      base: data.totalPagesStored,
      ratePerSecond: data.totalPagesStoredLast3Months / SECONDS_PER_91_25_DAYS,
    }
  }
  return {
    base: byType.total,
    ratePerSecond: byType.last3Months / SECONDS_PER_91_25_DAYS,
  }
}

function computeDisplayFromData(
  d: CurrentData,
  role: RoleKey,
  tUnix: number,
  cacheAt: number
): CurrentDataDisplay {
  const totalRate = d.totalPagesStoredLast3Months / SECONDS_PER_91_25_DAYS
  const estimatedTotalPages = Math.floor(
    estimateAtTime(d.totalPagesStored, totalRate, cacheAt, tUnix)
  )
  const { base: pagesBase, ratePerSecond: pagesRate } = getPagesBaseAndRate(d, role)
  const pages = Math.floor(estimateAtTime(pagesBase, pagesRate, cacheAt, tUnix))
  const transactionsRate =
    d.platformTransactionsTotalLast3MonthsAud / SECONDS_PER_91_25_DAYS
  const transactions = estimateAtTime(
    d.platformTransactionsTotalAud,
    transactionsRate,
    cacheAt,
    tUnix
  )
  const threeMonths = d.threeMonthsSeconds ?? SECONDS_PER_91_25_DAYS
  const timeSaved = d.estimatedTotalTimeSavedSeconds
  const timeSavedBase = timeSaved?.totalTimeSavedSeconds ?? 0
  const timeSavedRate = timeSaved
    ? timeSaved.totalTimeSavedSecondsLast3Months / threeMonths
    : 0
  const estimatedTotalTimeSavedSeconds = estimateAtTime(
    timeSavedBase,
    timeSavedRate,
    cacheAt,
    tUnix
  )
  return {
    estimatedTotalPages,
    estimatedPages: pages,
    activeDealerships: d.activeDealershipsCount,
    estimatedTransactionsAud: transactions,
    estimatedTotalTimeSavedSeconds,
  }
}

export function getFallbackDisplay(roleKey: RoleKey): CurrentDataDisplay {
  return computeDisplayFromData(
    FALLBACK_CURRENT_DATA,
    roleKey,
    FALLBACK_SERVER_TIME,
    FALLBACK_CACHED_AT
  )
}

export function useCurrentData(roleKey: RoleKey): UseCurrentDataResult {
  const [data, setData] = useState<CurrentData | null>(FALLBACK_CURRENT_DATA)
  const [cachedAt, setCachedAt] = useState<number>(FALLBACK_CACHED_AT)
  const [serverTime, setServerTime] = useState<number>(FALLBACK_SERVER_TIME)
  const [responseReceivedAt, setResponseReceivedAt] = useState<number>(() =>
    typeof Date.now === 'function' ? Date.now() / 1000 : FALLBACK_SERVER_TIME
  )
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [display, setDisplay] = useState<CurrentDataDisplay | null>(() =>
    getFallbackDisplay(roleKey)
  )
  const refetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const tickIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const hasEndpoint = !!getCurrentDataEndpoint()

  const computeDisplay = useCallback(
    (
      d: CurrentData,
      role: RoleKey,
      tUnix: number,
      cacheAt: number
    ): CurrentDataDisplay => computeDisplayFromData(d, role, tUnix, cacheAt),
    []
  )

  const scheduleRefetch = useCallback(() => {
    if (refetchTimeoutRef.current) {
      clearTimeout(refetchTimeoutRef.current)
      refetchTimeoutRef.current = null
    }
    if (!cachedAt || !serverTime || !responseReceivedAt) return

    const delaySeconds =
      CACHE_TTL_SECONDS +
      randomBetween(REFETCH_DELAY_MIN, REFETCH_DELAY_MAX) -
      (serverTime - cachedAt)
    const delayMs = Math.max(1000, Math.floor(delaySeconds * 1000))

    refetchTimeoutRef.current = setTimeout(async () => {
      refetchTimeoutRef.current = null
      const result = await fetchCurrentData()
      if (result.ok) {
        const receivedAt = Date.now() / 1000
        setData(result.data)
        setCachedAt(result.cachedAt)
        setServerTime(result.serverTime)
        setResponseReceivedAt(receivedAt)
        setError(false)
        const tUnix = result.serverTime + (receivedAt - result.serverTime)
        setDisplay(computeDisplay(result.data, roleKey, tUnix, result.cachedAt))
      }
      // -- [Effect will reschedule next refetch when state updates]
    }, delayMs)
  }, [cachedAt, serverTime, responseReceivedAt, roleKey, computeDisplay])

  function applyFallbackData() {
    const receivedAt = Date.now() / 1000
    setData(FALLBACK_CURRENT_DATA)
    setCachedAt(FALLBACK_CACHED_AT)
    setServerTime(FALLBACK_SERVER_TIME)
    setResponseReceivedAt(receivedAt)
    const tUnix = FALLBACK_SERVER_TIME + (receivedAt - FALLBACK_SERVER_TIME)
    setDisplay(computeDisplay(FALLBACK_CURRENT_DATA, roleKey, tUnix, FALLBACK_CACHED_AT))
  }

  // -- [Initial fetch and refetch scheduler when we have data]
  useEffect(() => {
    if (!hasEndpoint) {
      setLoading(false)
      setError(true)
      applyFallbackData()
      return
    }

    let cancelled = false

    async function run() {
      const result = await fetchCurrentData()
      if (cancelled) return
      setLoading(false)
      if (result.ok) {
        const receivedAt = Date.now() / 1000
        setData(result.data)
        setCachedAt(result.cachedAt)
        setServerTime(result.serverTime)
        setResponseReceivedAt(receivedAt)
        setError(false)
        const tUnix = result.serverTime
        setDisplay(computeDisplay(result.data, roleKey, tUnix, result.cachedAt))
      } else {
        setError(true)
        applyFallbackData()
      }
    }

    run()
    return () => {
      cancelled = true
    }
  }, [hasEndpoint]) // eslint-disable-line react-hooks/exhaustive-deps -- only when endpoint is configured; roleKey only affects display

  // -- [Re-schedule refetch when cachedAt/serverTime/responseReceivedAt change; skip when no endpoint]
  useEffect(() => {
    if (!hasEndpoint || !data || !cachedAt || !serverTime || !responseReceivedAt) return
    scheduleRefetch()
    return () => {
      if (refetchTimeoutRef.current) {
        clearTimeout(refetchTimeoutRef.current)
        refetchTimeoutRef.current = null
      }
    }
  }, [hasEndpoint, cachedAt, serverTime, responseReceivedAt, scheduleRefetch, data])

  // -- [10s tick: recompute display from current estimated time]
  useEffect(() => {
    if (!data || !cachedAt || !responseReceivedAt || !serverTime) return

    const tick = () => {
      const nowClient = Date.now() / 1000
      const tUnix = serverTime + (nowClient - responseReceivedAt)
      setDisplay(computeDisplay(data, roleKey, tUnix, cachedAt))
    }

    tick()
    tickIntervalRef.current = setInterval(tick, TICK_INTERVAL_MS)
    return () => {
      if (tickIntervalRef.current) {
        clearInterval(tickIntervalRef.current)
        tickIntervalRef.current = null
      }
    }
  }, [data, cachedAt, serverTime, responseReceivedAt, roleKey, computeDisplay])

  // -- [When roleKey changes, recompute display from same data]
  useEffect(() => {
    if (!data || !cachedAt || !serverTime || !responseReceivedAt) return
    const nowClient = Date.now() / 1000
    const tUnix = serverTime + (nowClient - responseReceivedAt)
    setDisplay(computeDisplay(data, roleKey, tUnix, cachedAt))
  }, [roleKey, data, cachedAt, serverTime, responseReceivedAt, computeDisplay])

  const useFallback = !display && (error || !hasEndpoint)

  return {
    display,
    data,
    loading,
    error,
    useFallback,
  }
}
